import { css } from '@emotion/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { Content } from '../Content';
import { theme } from '../theme';

import { CartSection } from './CartSection';

const styles = {
  wrapper: css`
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;

    @media (max-width: ${theme.breakpoints.mobileL}) {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100;
      width: 100vw;
      background: ${theme.colors.white};
      padding-bottom: env(safe-area-inset-bottom, 16px);
    }
  `,
  contentWrapper: (isExpandable: boolean) => css`
    background-color: ${theme.colors.white};
    min-height: 96px;
    border-top: 1px solid ${theme.colors.border};
    display: flex;

    @media (max-width: ${theme.breakpoints.laptop}) {
      border-top: ${isExpandable ? '0' : `1px solid ${theme.colors.border}`};
    }
  `,
  content: css`
    display: flex;
    gap: ${theme.spacing.lg};
    padding-top: ${theme.spacing.md};
    padding-bottom: ${theme.spacing.md};

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      gap: 0;
    }
  `,
  expandableWrapper: (isExpanded: boolean) => css`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    transform: translateY(-100%);
    z-index: 2;

    @media (max-width: ${theme.breakpoints.laptop}) {
      right: ${isExpanded ? theme.spacing.md : 0};
      left: ${isExpanded ? theme.spacing.md : 0};
    }
  `,
  expandableContent: css`
    padding: ${theme.spacing.lg};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borders.radius.md} ${theme.borders.radius.md} 0 0;
    border: 1px solid ${theme.colors.border};
    border-bottom: none;
    max-height: 65vh;
    overflow-y: auto;

    @media (max-width: ${theme.breakpoints.laptop}) {
      border-radius: 0;
      padding: ${theme.spacing.md};
    }
  `,
  expandButton: css`
    width: 120px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borders.radius.lg} ${theme.borders.radius.lg} 0 0;
    border: 1px solid ${theme.colors.border};
    border-bottom: none;
    box-sizing: border-box;
    border-bottom: 0;

    &:focus,
    &:active,
    &:hover {
      background-color: ${theme.colors.white};
    }

    > span {
      height: 2px;
      width: 42px;
      background-color: ${theme.colors.border};
      border-radius: 1px;
    }

    @media (max-width: ${theme.breakpoints.laptop}) {
      width: 100%;
    }
  `,
  children: css`
    width: 100%;
  `,
  actions: css`
    display: flex;
    align-items: center;

    @media (max-width: ${theme.breakpoints.tablet}) {
      > * {
        margin-top: ${theme.spacing.lg};
      }
    }
  `,
};

type Props = PropsWithChildren<{
  Expanded?: React.ComponentType;
  Action?: React.ComponentType;
}>;

export function Cart({ children, Expanded, Action }: Props) {
  const cartRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartRef.current && event.target instanceof Node && !cartRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={cartRef} css={styles.wrapper}>
      {!!Expanded && (
        <div css={styles.expandableWrapper(isExpanded)}>
          <button className="reset" css={styles.expandButton} onClick={() => setIsExpanded(!isExpanded)}>
            <span />
          </button>
          {isExpanded && (
            <Content css={styles.expandableContent}>
              <Expanded />
            </Content>
          )}
        </div>
      )}
      <div css={styles.contentWrapper(!!Expanded)}>
        <Content css={styles.content}>
          <CartSection css={styles.children}>{children}</CartSection>
          {Action && (
            <div css={styles.actions}>
              <Action />
            </div>
          )}
        </Content>
      </div>
    </div>
  );
}
